import {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FastImageProps, ImageStyle } from 'react-native-fast-image';
import { imageResize } from '../../functions/image';
import { safeMarkDevFlag } from '../../utils/safeMarkDevFlag';

export type PopImageStyle = ImageStyle;

export type PopImageProps = Omit<FastImageProps, 'source'> & {
  uri?: string;
  animatedImageEnabled?: boolean;
  resizeTo?: { width: number; height: number };
  decodeAsync?: boolean;
  decodeWidth?: number;
  decodeHeight?: number;
  priority?: 'low' | 'normal' | 'high';
  defaultBlurhash?: string;
  blurhashResizeMode?: 'cover' | 'contain' | 'stretch' | 'center';
  blurhashVisibility?: 'always' | 'auto';
  // if lazy loading is enabled, disabled by default
  lazyLoad?: boolean;
  // if lazy loading fade effect is enabled, disabled by default
  lazyLoadTransition?: boolean | string;
  // inline styles of the underlying image on web, ignored on native
  webImageStyle?: CSSProperties;
  // class name of the underlying image element on web, ignored on native
  webImageClassName?: string;
};

const loadedImageUrls: { [url: string]: boolean } = {};

const kDisableResize = safeMarkDevFlag(false);

export const usePopImage = (
  uri: string | undefined,
  resizeTo: { width: number; height: number } | undefined,
  onLoadFromProps: ((event: any) => void) | undefined,
  options: {
    blurhashVisibility?: 'always' | 'auto';
    defaultBlurhash?: string;
  } = {},
) => {
  if (uri === '') uri = undefined;

  const defaultBlurhash = options?.defaultBlurhash ?? '';

  const blurhashVisibility = options?.blurhashVisibility ?? 'auto';
  const hash = useMemo(() => {
    try {
      if (!uri) return defaultBlurhash;
      if (!uri.startsWith('http')) return defaultBlurhash;
      const result = decodeURIComponent(uri.split('#')[1]);
      // in some cases, backend will return 'undefined'
      if (result === 'undefined') return defaultBlurhash;
      return result.includes('009jvfQfQfQfQfQfQfQfQfQfQfQ')
        ? defaultBlurhash
        : result;
    } catch (e) {
      return defaultBlurhash;
    }
  }, [defaultBlurhash, uri]);
  const imageUrl = useMemo(
    () => (resizeTo && !kDisableResize ? imageResize(uri, resizeTo)! : uri),
    [uri, resizeTo],
  );
  const urlRef = useRef(imageUrl);
  urlRef.current = imageUrl;
  const [loaded, setLoaded] = useState(false);
  const [showBlur, setShowBlur] = useState(
    blurhashVisibility === 'always' ||
      (Boolean(hash) &&
        Boolean(imageUrl) &&
        !Boolean(loadedImageUrls[imageUrl!])),
  );
  useEffect(() => {
    setShowBlur(
      blurhashVisibility === 'always' ||
        (Boolean(hash) &&
          Boolean(imageUrl) &&
          !Boolean(loadedImageUrls[imageUrl!])),
    );
  }, [blurhashVisibility, hash, imageUrl]);
  const onLoaded = useCallback(
    (event: any) => {
      setLoaded(true);
      if (blurhashVisibility !== 'always') {
        setShowBlur(false);
      }
      if (urlRef.current) {
        loadedImageUrls[urlRef.current] = true;
      }
      onLoadFromProps?.(event);
    },
    [onLoadFromProps, blurhashVisibility],
  );
  return {
    hash,
    showBlur,
    imageUrl,
    onLoaded,
    loaded,
  };
};
